import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import {
  type ApiOutputs,
  privateTemplateListSchema,
  privateTemplateSchema,
  publicIdSchema,
} from "@rosc/contracts";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import type { Template } from "@/common/types/templates";
import { referenceClient } from "@/lib/orpc/client";
import { queryClient } from "@/lib/query-client";
import {
  getVisitScope,
  useVisitScope,
  type VisitScope,
} from "@/lib/visit-queries";
import { checkPublicPage } from "./public-pagination";

export const toTemplate = (value: ApiOutputs["getTemplate"]): Template => ({
  public_id: value.public_id,
  name: value.name,
  headers: value.headers,
  optionalText: value.optionalText ?? undefined,
  isDefault: value.isDefault ? 1 : 0,
  createDate: value.createdAt,
});
const orpc = createTanstackQueryUtils(referenceClient, {
  scoped: {
    listTemplates: {
      queryInterceptors: [
        async (options) => {
          const items: ApiOutputs["getTemplate"][] = [];
          let cursor: string | undefined;
          const seen = new Set<string>();
          do {
            options.fnContext.signal.throwIfAborted();
            const page = privateTemplateListSchema.parse(
              await options.next({
                ...options,
                input: {
                  query: { limit: "100", ...(cursor ? { cursor } : {}) },
                },
              }),
            );
            checkPublicPage(page, cursor, seen);
            items.push(...page.items);
            cursor = page.nextCursor ?? undefined;
          } while (cursor);
          return { items, nextCursor: null };
        },
      ],
    },
    getTemplate: {
      queryInterceptors: [
        async (options) => {
          const value = privateTemplateSchema.parse(await options.next());
          if (
            typeof options.input === "symbol" ||
            value.public_id !== options.input.params.public_id
          )
            throw Error("Unexpected template identity");
          return value;
        },
      ],
    },
  },
});
const key = (scope: VisitScope): readonly unknown[] => [
  "template-reads",
  scope.uid,
  scope.accountPublicId,
  scope.generation,
];
const disabledScope: VisitScope = {
  uid: "",
  accountPublicId: "",
  generation: 0,
};
export const templateListOptions = (scope: VisitScope) =>
  orpc.listTemplates.queryOptions({
    queryKey: [...key(scope), "list"],
    input: { query: { limit: "100" } },
    context: { expectedUserUid: scope.uid },
    staleTime: 30_000,
    retry: false,
  });
export async function refreshTemplateReads(
  scope: VisitScope | null = getVisitScope(),
): Promise<Template[]> {
  if (!scope || getVisitScope() !== scope) return [];
  await queryClient.cancelQueries({ queryKey: key(scope) });
  if (getVisitScope() !== scope) return [];
  await queryClient.invalidateQueries(
    { queryKey: key(scope) },
    { cancelRefetch: false },
  );
  if (getVisitScope() !== scope) return [];
  try {
    const page = await queryClient.fetchQuery(templateListOptions(scope));
    return getVisitScope() === scope ? page.items.map(toTemplate) : [];
  } catch {
    return [];
  }
}
export function useTemplateList() {
  const scope = useVisitScope();
  const query = useQuery({
    ...templateListOptions(scope ?? disabledScope),
    enabled: !!scope,
  });
  const refresh = useCallback(() => refreshTemplateReads(scope), [scope]);
  return {
    templates: scope ? (query.data?.items.map(toTemplate) ?? []) : [],
    loading: !!scope && query.isPending,
    error: query.isError,
    ready: !!scope && !!query.data,
    refresh,
  };
}
// The template list for a component that may render outside the app's
// QueryClientProvider (the new-visit sheet in isolation): the app's own
// client, so it shares the cache, and nothing is fetched while disabled.
export function useOptionalTemplateList(enabled: boolean): {
  templates: Template[];
  loading: boolean;
  error: boolean;
} {
  const scope = useVisitScope();
  const active = enabled && !!scope;
  const query = useQuery(
    {
      ...templateListOptions(scope ?? disabledScope),
      enabled: active,
    },
    queryClient,
  );
  return {
    templates: active ? (query.data?.items.map(toTemplate) ?? []) : [],
    loading: active && query.isPending,
    error: active && query.isError,
  };
}
export function useTemplateRead(id: string) {
  const scope = useVisitScope();
  const valid = publicIdSchema.safeParse(id).success;
  const query = useQuery(
    orpc.getTemplate.queryOptions({
      queryKey: [...key(scope ?? disabledScope), "detail", id],
      input: { params: { public_id: String(id) }, query: {} },
      context: { expectedUserUid: scope?.uid },
      enabled: !!scope && valid,
      retry: false,
    }),
  );
  const refresh = useCallback(() => refreshTemplateReads(scope), [scope]);
  return {
    result:
      scope && valid && !query.isError && query.data
        ? toTemplate(query.data)
        : null,
    loading: !!scope && valid && query.isPending,
    error: !valid || query.isError,
    refresh,
  };
}
